/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { ImmutableObject } from "@hookstate/core";
import { useNavigate } from "react-router-dom";
import { Catalog } from "../../hooks/useCatalogState";

// Import Swiper
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';
import { Pagination, Navigation, Autoplay, EffectFade, EffectCards, EffectCoverflow } from 'swiper';
import { Button } from "@mui/material";
import { useStorefrontSettingsState } from "../../hooks/useStorefrontSettingsState";
import { AutoplayOptions, SwiperModule } from "swiper/types";
import { SafeHtmlContent } from "../SafeHtmlContent";
import { useThemeState } from "../../hooks/useThemeState";
import { useTranslate } from "../../hooks/useTranslate/useTranslate";
import { CategoryTreeGrid } from "./CategoryTreeGrid";
import { Images } from "lucide-react";
//-------------------------------------------------------------------------------------------------------
export type LandingPageProps = {
    catalog: Catalog | ImmutableObject<Catalog>;
    showCatalogName: boolean;
    showCategoryTreeGrid?: boolean;
}
export const LandingPage: React.FC<LandingPageProps> = props => {
    const translate = useTranslate();
    const themeState = useThemeState();
    const isDarkTheme = themeState.isDark();
    const navigate = useNavigate();

    const storefrontSettingsState = useStorefrontSettingsState();
    const swiperModules: SwiperModule[] = [];
    let autoplayOptions: AutoplayOptions | undefined;
    const settings = storefrontSettingsState.get().settings;
    if (settings && settings.AutoplayImagesOnLandingPage) {
        swiperModules.push(Autoplay);
        autoplayOptions = {
            delay: settings.AutoplaySpeed * 1000,
        };
    }
    swiperModules.push(Pagination);
    swiperModules.push(Navigation);
    swiperModules.push(EffectFade, EffectCards, EffectCoverflow);
    const landingPageShowCategoryBrowser = (settings?.LandingPageShowCategoryBrowser ?? true);
    const homepageCatalogItemHeight = storefrontSettingsState.get().settings?.HomepageCatalogItemHeight ?? 'md';
    let homepageCatalogItemHeightPx = 240;
    if (homepageCatalogItemHeight === 'xs') {
        homepageCatalogItemHeightPx = 128;
    }
    else if (homepageCatalogItemHeight === 'sm') {
        homepageCatalogItemHeightPx = 240;
    }
    else if (homepageCatalogItemHeight === 'md') {
        homepageCatalogItemHeightPx = 320;
    }
    else if (homepageCatalogItemHeight === 'lg') {
        homepageCatalogItemHeightPx = 480;
    }
    else if (homepageCatalogItemHeight === 'xl') {
        homepageCatalogItemHeightPx = 640;
    }
    else if (homepageCatalogItemHeight === '2xl') {
        homepageCatalogItemHeightPx = 720;
    }
    let catalogItemHeightPx = ((props.showCatalogName || (!landingPageShowCategoryBrowser)) ? homepageCatalogItemHeightPx : 128);

    const imagesTransitionEffect = storefrontSettingsState.get().settings?.ImagesTransitionEffect ?? 'fade';
    const imagesTransitionSpeed = parseFloat(storefrontSettingsState.get().settings?.ImagesTransitionSpeed ?? '1000') ?? 1000;
    const images = props.catalog.Images?.split(',') ?? [];
    const nonEmptyImages = images.filter(img => img && img.trim());
    const shouldLoop = nonEmptyImages.length > 1;

    return (
        <div
            css={css`
                .swiper-slide img {
                    cursor: ${props.showCatalogName ? 'pointer' : 'default'} !important;
                }
                padding: 8px;
            `}>
                         {(props.showCatalogName) && (<>
                 <div
                     css={css`
                     display:flex;
                     align-items:center;
                     justify-content:center;
                     font-size: 1.8em;
                     font-weight: 700;
                     padding: 0 0 1rem 0;
                     color: ${isDarkTheme ? '#fff' : '#2c3e50'};
                     text-shadow: ${isDarkTheme ? '0 1px 2px rgba(0,0,0,0.3)' : '0 1px 2px rgba(0,0,0,0.1)'};
                     cursor: pointer;
                     transition: all 0.3s ease;
                     white-space: nowrap;
                     &:hover {
                         color: ${isDarkTheme ? '#3498db' : '#007bff'};
                         transform: scale(1.02);
                     }
                     
                     @media (max-width: 768px) {
                         font-size: 1.5em;
                     }
                 `}
                     onClick={() => navigate(`/inventory/${props.catalog.Id}/index`)}
                 >
                     {props.catalog?.Name ?? ''}
                 </div>
             </>)}
            
            {/*autoplay={{*/}
            {/*    delay: 10000,*/}
            {/*    stopOnLastSlide: false*/}
            {/*}}*/}

            {(props.catalog.Images?.length > 0 || true) && (
                                 <div css={css`
                     margin: 1rem 0 0 0;
                     position: relative;
                     border-radius: 12px;
                     overflow: hidden;
                     max-width: 100%;
                     width: 100%;
                     /*box-shadow: ${isDarkTheme 
                         ? '0 8px 32px rgba(0,0,0,0.3)' 
                         : '0 8px 32px rgba(0,0,0,0.1)'
                     };*/
                    user-select: none;
                    
                    .swiper {
                        width: 100% !important;
                        max-width: 100% !important;
                        overflow: hidden !important;
                    }
                    
                    .swiper-slide {
                        width: 100% !important;
                        max-width: 100% !important;
                    }
                     
                     /* Fade effect specific fixes - slide opacity transitions */
                     .swiper-slide {
                         opacity: 0;
                         transition: opacity 0.6s ease;
                     }
                     
                     .swiper-slide-active {
                         opacity: 1;
                     }
                    
                    /* Custom Navigation Buttons - now handled globally in main.css */
                    
                    /* Custom Pagination - now handled globally in main.css */
                    
                    /* Image Container */
                    .swiper-slide {
                        border-radius: 12px;
                        overflow: hidden;
                        /*background: ${isDarkTheme 
                            ? 'rgba(255,255,255,0.05)' 
                            : 'rgba(255,255,255,0.8)'
                        };*/
                        backdrop-filter: blur(10px);
                    }
                    
                    /* Hover effect for images */
                    .swiper-slide img {
                        transition: transform 0.3s ease;
                    }
                    
                    .swiper-slide:hover img {
                        transform: ${props.showCatalogName ? 'scale(1.05)' : 'none'};
                    }
                `}>
                     <Swiper
                         speed={imagesTransitionSpeed}
                         pagination={{
                             dynamicBullets: true,
                             type: "bullets",
                             clickable: true
                         }}
                         autoplay={autoplayOptions}
                         spaceBetween={0}
                         navigation={true}
                         grabCursor={true}
                         centeredSlides={false}
                         centerInsufficientSlides={false}
                         slidesPerView={1}
                         modules={swiperModules}
                         loop={shouldLoop}
                         effect={imagesTransitionEffect}
                         fadeEffect={{
                             crossFade: true
                         }}
                         onClick={async _e => {
                             if (props.showCatalogName) {
                                 // Navigate directly - let the destination page handle state management
                                 navigate(`/inventory/${props.catalog.Id}/index`);
                             }
                         }}
                     >
                         {((props.catalog.Images?.split(',') ?? []).filter(img => img && img.trim()).length > 0) ? (
                             // Show actual images if available
                             (props.catalog.Images?.split(',') ?? []).filter(img => img && img.trim()).map((image, index, images) => (
                                 <SwiperSlide key={index}>
                                     <div css={css`
                                         display: flex;
                                         align-items: center;
                                         justify-content: center;
                                         border-radius: 12px;
                                         backdrop-filter: blur(10px);
                                     `}>
                                         <img
                                             src={image} 
                                             alt={props.catalog.Name}
                                             css={css`
                                                 max-width: 100%;
                                                 height: ${catalogItemHeightPx}px;
                                                 object-fit: contain;
                                                 transition: all 0.3s ease;
                                                 cursor: ${props.showCatalogName ? 'pointer' : 'false'};
                                                 
                                                 &:hover {
                                                     transform: scale(1.05);
                                                 }
                                             `}
                                         />
                                     </div>
                                 </SwiperSlide>
                             ))
                         ) : (
                                                           // Show placeholder when no images
                              <SwiperSlide>
                                  <div css={css`
                                      height: ${((props.showCatalogName || (!landingPageShowCategoryBrowser)) ? '240px' : '128px')};
                                      display: flex;
                                      flex-direction: column;
                                      align-items: center;
                                      justify-content: center;
                                      background: ${isDarkTheme 
                                          ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)' 
                                          : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.8) 100%)'
                                      };
                                      border-radius: 12px;
                                      backdrop-filter: blur(10px);
                                      border: 2px solid ${isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
                                      position: relative;
                                      overflow: hidden;
                                      cursor: ${props.showCatalogName ? 'pointer' : 'false'};
                                      
                                      &::before {
                                          content: '';
                                          position: absolute;
                                          top: 0;
                                          left: 0;
                                          right: 0;
                                          bottom: 0;
                                          background: ${isDarkTheme 
                                              ? 'radial-gradient(circle at 30% 20%, rgba(52, 152, 219, 0.1) 0%, transparent 50%)' 
                                              : 'radial-gradient(circle at 30% 20%, rgba(0, 123, 255, 0.05) 0%, transparent 50%)'
                                          };
                                          pointer-events: none;
                                      }
                                  `}>
                                      <div css={css`
                                          display: flex;
                                          flex-direction: column;
                                          align-items: center;
                                          justify-content: center;
                                          text-align: center;
                                          z-index: 1;
                                      `}>
                                          <div css={css`
                                              width: 80px;
                                              height: 80px;
                                              border-radius: 50%;
                                              background: ${isDarkTheme 
                                                  ? 'linear-gradient(135deg, rgba(52, 152, 219, 0.2), rgba(52, 152, 219, 0.1))' 
                                                  : 'linear-gradient(135deg, rgba(0, 123, 255, 0.1), rgba(0, 123, 255, 0.05))'
                                              };
                                              display: flex;
                                              align-items: center;
                                              justify-content: center;
                                              margin-bottom: 1.5rem;
                                              border: 2px solid ${isDarkTheme ? 'rgba(52, 152, 219, 0.3)' : 'rgba(0, 123, 255, 0.2)'};
                                              width: 50px;
                                              height: 50px;
                                              margin-bottom: 0.75rem;
                                          `}>
                                            <Images 
                                                size={32}
                                                color={isDarkTheme ? 'oklch(0.9 0 0)' : 'rgba(0, 123, 255, 0.7)'}
                                                css={css`
                                                    width: 20px;
                                                    height: 20px;
                                                `}
                                            />
                                          </div>
                                          
                                          <div css={css`
                                              font-size: 1.4rem;
                                              font-weight: 700;
                                              color: ${isDarkTheme ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)'};
                                              margin-bottom: 0.5rem;
                                              text-shadow: ${isDarkTheme ? '0 1px 2px rgba(0,0,0,0.3)' : '0 1px 2px rgba(0,0,0,0.1)'};
                                              font-size: 1.1rem;
                                          `}>
                                              {props.catalog.Name}
                                          </div>

                                          {props.showCatalogName && (
                                              <div css={css`
                                                  font-size: 0.95rem;
                                                  color: ${isDarkTheme ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)'};
                                                  font-weight: 500;
                                                  max-width: 200px;
                                                  font-size: 0.8rem;
                                                  max-width: 160px;
                                              `}>
                                                    {translate('Browse our catalog')}
                                              </div>
                                          )}
                                          
                                          {props.showCatalogName && (
                                              <div css={css`
                                                  margin-top: 1rem;
                                                  padding: 0.5rem 1rem;
                                                  background: ${isDarkTheme 
                                                      ? 'rgba(52, 152, 219, 0.15)' 
                                                      : 'rgba(0, 123, 255, 0.08)'
                                                  };
                                                  border-radius: 20px;
                                                  border: 1px solid ${isDarkTheme ? 'rgba(52, 152, 219, 0.3)' : 'rgba(0, 123, 255, 0.2)'};
                                                  margin-top: 0.5rem;
                                                  padding: 0.3rem 0.6rem;
                                              `}>
                                                  <div css={css`
                                                      font-size: 0.8rem;
                                                      color: ${isDarkTheme ? 'rgba(52, 152, 219, 0.9)' : 'rgba(0, 123, 255, 0.8)'};
                                                      font-weight: 600;
                                                      font-size: 0.7rem;
                                                  `}>
                                                        {translate('Click to explore')}
                                                  </div>
                                              </div>
                                          )}
                                      </div>
                                  </div>
                              </SwiperSlide>
                         )}
                     </Swiper>
                </div>
            )}

            {(props.showCatalogName) && (<>
                <div
                    css={css`
                        display:flex;
                        align-items:center;
                        justify-content:center;
                        margin: 1.5rem 0 0 0;
                    `}>
                    <Button
                        variant="contained"
                        size="large"
                        css={css`
                            background: var(--color-sf-homepage-hero-btn-bg) !important;
                            color: var(--color-sf-homepage-hero-btn-text) !important;
                            font-weight: 600 !important;
                            padding: 0.75rem 2rem !important;
                            border-radius: 8px !important;
                            text-transform: none !important;
                            font-size: 1rem !important;
                            box-shadow: ${isDarkTheme 
                                ? '0 4px 12px olch(1 0 0 / 0.3)' 
                                : '0 4px 12px olch(1 0 0 / 0.3)'
                            } !important;
                            
                            &:hover {
                                background: ${isDarkTheme ? '#2980b9' : '#0056b3'} !important;
                                box-shadow: ${isDarkTheme 
                                    ? '0 6px 16px rgba(52, 152, 219, 0.4)' 
                                    : '0 6px 16px rgba(0, 123, 255, 0.4)'
                                } !important;
                                transform: translateY(-1px);
                            }
                        `}
                        onClick={async e => {
                            // Navigate directly - let the destination page handle state management
                            navigate(`/inventory/${props.catalog.Id}/index`);
                        }}>
                        {translate('View Catalog')}
                    </Button>
                </div>
            </>)}

            {(props.catalog.LandingPageHtml && props.catalog.LandingPageHtml.length > 0) && (<>
                <div
                    css={css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1rem;
                    line-height: 1.3rem;
                    font-weight: 400;
                    padding: 0 0 10px 0;
                `}>
                    <SafeHtmlContent htmlContent={props.catalog.LandingPageHtml} />
                </div>
            </>)}
            
            {/* Category Tree Grid */}
            {(landingPageShowCategoryBrowser && props.showCategoryTreeGrid) && (
                <CategoryTreeGrid 
                    catalog={props.catalog}
                    showTitle={true}
                />
            )}
        </div>
    );
}